<template>
  <div>
    <Message
      v-if="isBulkCampaign"
      severity="warn"
      :closable="false"
      v-html="t('campaigns.bulk.messages.alertFiltersMarch2025')"
      style="padding: 1rem 1.5rem;"
    />
    <FilterGlobalInformations
      v-model="commonFields"
      :form-validation="formValidationErrors"
      :is-edit="isEdit"
    />

    <div class="field-group pt-2">
      <div>
        <div class="field-group-content">
          <div class="field-wrapper">
            <ButtonGroup
              v-model="grantAdditionalDelay"
              :options="options"
              title="automatedScenarios.fields.grantAdditionalDelay"
            />
            <FieldErrors
              :key="componentFieldErrorsKey"
              :errors="formValidation"
              field="grant_additional_delay"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="grantAdditionalDelay === '1'"
      class="field-group pt-2"
    >
      <div>
        <div class="field-group-content">
          <div class="field-wrapper">
            <PeriodSelector
              v-model="delay"
              :units="units"
              title="automatedScenarios.fields.duration"
            />
            <FieldErrors
              :key="componentFieldErrorsKey"
              :errors="formValidation"
              field="extra_time"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <FilterPanelContent
    :active-items="activeItemsFilterPanel"
    :panels="['include', 'exclude']"
  >
    <template #include>
      <FilterSegmentForm
        :segments="includeSegmentsForForm"
        :active-segments="activeIncludeSegments"
        :items="filters.include"
        :scenario-type="campaignType"
        :current-step-id="operatorId"
        @add:segment="handleAddSegment('include', $event)"
        @remove:segment="handleRemoveSegment('include', $event.segmentName, $event.number)"
      />
    </template>
    <template #exclude>
      <FilterSegmentForm
        :segments="excludeSegmentsForForm"
        :active-segments="activeExcludeSegments"
        :items="filters.exclude"
        :scenario-type="campaignType"
        :current-step-id="operatorId"
        @add:segment="handleAddSegment('exclude', $event)"
        @remove:segment="handleRemoveSegment('exclude', $event.segmentName, $event.number)"
      />
    </template>
  </FilterPanelContent>
  <FieldErrors
    :key="componentFieldErrorsKey"
    :errors="formValidation"
    field="inclusion"
  />
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeMount,
  PropType,
  Ref,
  ref,
  SetupContext,
  watch,
} from 'vue';
import type { FilterSegment, Period } from '@/types/flow-types';
import { useI18n } from 'vue-i18n';
import {
  addInArraySegment,
  getActiveSegments,
  removeInSegment,
} from '@/composables/flows/useFlowSegments';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { asInt } from '@/helpers';
import Message from 'primevue/message';
import getSegments from '@/components/flows/operators/filters/PurchaseHistory/segments';
import {
  ButtonGroup,
  PeriodSelector,
  FilterGlobalInformations,
  FilterPanelContent,
  FilterSegmentForm,
} from '@/components/flows/operators/shared';
import { TypeCampaignEnum } from '@/types';
import type { PurchaseHistoryData, PurchaseHistoryFilters } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'FilterPurchaseHistoryForm',

  components: {
    FieldErrors,
    FilterGlobalInformations,
    FilterPanelContent,
    ButtonGroup,
    PeriodSelector,
    FilterSegmentForm,
    Message,
  },

  props: {
    modelValue: {
      type: Object as PropType<PurchaseHistoryData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },

    campaignType: {
      type: String as PropType<TypeCampaignEnum>,
      default: TypeCampaignEnum.AUTOMATION,
    },

    operatorId: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: PurchaseHistoryData; formValidation: any; campaignType: TypeCampaignEnum; operatorId: string | null }, context: SetupContext) {
    const { t } = useI18n();
    const componentFieldErrorsKey = ref(0);
    const formValidationErrors = ref(props.formValidation);

    const isBulkCampaign = computed(() => props.campaignType === TypeCampaignEnum.BULK);

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const isEdit = ref(!!props.modelValue.custom_title);

    const commonFields = ref({
      custom_title: props.modelValue.custom_title,
      custom_description: props.modelValue.custom_description,
      outputs_number: asInt(props.modelValue.outputs_number),
      initial_number_outputs: props.modelValue.initial_number_outputs,
    });
    const grantAdditionalDelay = ref(props.modelValue.grant_additional_delay);
    const delay = ref<Period>({
      unit: props.modelValue.extra_time ? props.modelValue.extra_time.unit : 'DAY',
      value: props.modelValue.extra_time ? props.modelValue.extra_time.value : 1,
    });
    const options = [
      { value: '0', label: t('no') },
      { value: '1', label: t('yes') },
    ];
    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
    ];

    const includeSegments: Ref<FilterSegment<PurchaseHistoryFilters>[]> = ref([]);
    const excludeSegments: Ref<FilterSegment<PurchaseHistoryFilters>[]> = ref([]);
    const includeSegmentsForForm = computed(() => includeSegments.value as unknown as FilterSegment<unknown>[]);
    const excludeSegmentsForForm = computed(() => excludeSegments.value as unknown as FilterSegment<unknown>[]);
    const activeIncludeSegments = computed(() => getActiveSegments<PurchaseHistoryFilters>(props.modelValue.inclusion, includeSegments.value));
    const activeExcludeSegments = computed(() => getActiveSegments<PurchaseHistoryFilters>(props.modelValue.exclusion, excludeSegments.value));

    const filters: Ref<{ include: PurchaseHistoryFilters; exclude: PurchaseHistoryFilters }> = ref({
      include: { ...props.modelValue.inclusion },
      exclude: { ...props.modelValue.exclusion },
    });

    const activeItemsFilterPanel = computed(() => ({
      include: activeIncludeSegments.value.length,
      exclude: activeExcludeSegments.value.length,
    }));

    onBeforeMount(async () => {
      includeSegments.value = await getSegments();
      excludeSegments.value = await getSegments();
    });

    const update = () => context.emit('update:modelValue', createDefaultData({
      ...commonFields.value,
      grant_additional_delay: grantAdditionalDelay.value,
      extra_time: delay.value,
      inclusion: filters.value.include,
      exclusion: filters.value.exclude,
    }));

    const handleAddSegment = (type: keyof { include: PurchaseHistoryFilters; exclude: PurchaseHistoryFilters },
      segment: FilterSegment<PurchaseHistoryFilters>) => {
      filters.value[type] = {
        ...filters.value[type],
        [segment.id]: { ...addInArraySegment<PurchaseHistoryFilters>(segment, filters.value[type][segment.id]) },
      };
      update();
    };

    const handleRemoveSegment = (type: keyof { include: PurchaseHistoryFilters; exclude: PurchaseHistoryFilters },
      segmentName: keyof PurchaseHistoryFilters, number: number) => {
      filters.value[type] = removeInSegment<PurchaseHistoryFilters>(filters.value[type], segmentName, number);
      update();
    };

    watch([commonFields, grantAdditionalDelay, delay], update, { deep: true });

    return {
      t,
      componentFieldErrorsKey,
      formValidationErrors,
      isBulkCampaign,
      isEdit,
      commonFields,
      grantAdditionalDelay,
      options,
      delay,
      units,
      includeSegments,
      includeSegmentsForForm,
      excludeSegments,
      excludeSegmentsForForm,
      activeIncludeSegments,
      activeExcludeSegments,
      filters,
      handleAddSegment,
      handleRemoveSegment,
      activeItemsFilterPanel,
    };
  },
});
</script>

<style lang="scss" scoped>
.field-group {
  margin-bottom: 1rem;
}
</style>
